import React, { useEffect, useReducer, useRef, type ReactNode } from 'react';
import { useLocation, type Location } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { subscribeToProjectWorkspaceCacheInvalidation } from '../../services/projectWorkspaceCache';

type CachedProjectRoute = {
  cacheKey: string;
  location: Location;
  uploadId?: string;
};

type ProjectCreationRouteCacheProps = {
  children: (location: Location) => ReactNode;
  path?: string;
};

const ProjectCreationRouteCache = ({
  children,
  path = '/project-creation',
}: ProjectCreationRouteCacheProps) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const cachedRouteRef = useRef<CachedProjectRoute | null>(null);
  const [, refreshCache] = useReducer(value => value + 1, 0);
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isProjectCreationRoute = normalizedPathname === path;

  if (isProjectCreationRoute) {
    const routeState = location.state as { uploadId?: string } | null;
    const uploadId = routeState?.uploadId;
    const cacheKey = uploadId || `${location.pathname}${location.search}`;

    cachedRouteRef.current = {
      cacheKey,
      location,
      uploadId,
    };
  }

  useEffect(
    () =>
      subscribeToProjectWorkspaceCacheInvalidation(({ uploadId }) => {
        const cachedRoute = cachedRouteRef.current;
        if (!cachedRoute || (uploadId && cachedRoute.uploadId !== uploadId)) {
          return;
        }

        cachedRouteRef.current = null;
        refreshCache();
      }),
    []
  );

  if (!isAuthenticated || user?.role !== 'doctor') {
    cachedRouteRef.current = null;
    return null;
  }

  const cachedRoute = cachedRouteRef.current;
  if (!cachedRoute) {
    return null;
  }

  return (
    <div
      aria-hidden={!isProjectCreationRoute}
      data-testid="project-workspace-cache"
      data-workspace-cache-key={cachedRoute.cacheKey}
      style={{ display: isProjectCreationRoute ? 'contents' : 'none' }}
    >
      <React.Fragment key={cachedRoute.cacheKey}>{children(cachedRoute.location)}</React.Fragment>
    </div>
  );
};

export default ProjectCreationRouteCache;
